/**
 * Client-side pre-flight check for a leader data file.
 *
 * Reuses the SAME schema validator the API enforces at commit (`validateLeaderFile` from
 * `@ait-tracker/shared`) so the browser can give immediate, structured feedback before any upload
 * round-trip — and so client and server can never disagree about what "valid" means. The server
 * remains the source of truth; this is a convenience gate, not a security control.
 */
// Import the schema module directly (not the package barrel): the barrel re-exports password.ts,
// which pulls node:crypto into the browser bundle. file-schema.ts is dependency-free.
import {validateSnapshotFile, MAX_FILE_BYTES} from '@ait-tracker/shared/src/file-schema';

export interface FileSummary {
  /** UTF-8 byte length of the file. */
  bytes: number;
  domains: number;
  jtbds: number;
  serviceAreas: number;
  phases: number;
}

export interface FileCheck {
  status: 'valid' | 'invalid';
  /** Human-readable problems (JSON parse / size / schema). Empty when valid. */
  errors: string[];
  /** Parsed JSON when the text parsed (even if schema-invalid); `null` if it could not parse. */
  parsed: unknown;
  /** Friendly counts, present only when `status === 'valid'`. */
  summary?: FileSummary;
}

/** UTF-8 byte length, environment-agnostic (browser + Node). */
function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Validate raw file text: enforces the size cap, parses JSON, then runs the shared schema
 * validator. Mirrors the order the API uses (size → parse → schema) so the messages a user sees
 * here match what the backend would have returned.
 */
export function checkSnapshotFileText(text: string): FileCheck {
  const bytes = byteLength(text);
  if (bytes > MAX_FILE_BYTES) {
    const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;
    return {
      status: 'invalid',
      parsed: null,
      errors: [`File is ${mb(bytes)} — exceeds the ${mb(MAX_FILE_BYTES)} limit.`]
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      status: 'invalid',
      parsed: null,
      errors: [`File is not valid JSON${e instanceof Error ? `: ${e.message}` : '.'}`]
    };
  }

  const result = validateSnapshotFile(parsed);
  if (!result.valid) {
    return {status: 'invalid', parsed, errors: result.errors};
  }

  return {status: 'valid', parsed, errors: [], summary: summarize(parsed, bytes)};
}

/** Count the headline entities for the success summary. Input is already schema-valid. */
function summarize(parsed: unknown, bytes: number): FileSummary {
  const file = parsed as {
    domains?: {jtbds?: unknown[]}[];
    serviceAreas?: unknown[];
    phases?: unknown[];
  };
  const domains = Array.isArray(file.domains) ? file.domains : [];
  const jtbds = domains.reduce((sum, d) => sum + (Array.isArray(d?.jtbds) ? d.jtbds.length : 0), 0);
  return {
    bytes,
    domains: domains.length,
    jtbds,
    serviceAreas: Array.isArray(file.serviceAreas) ? file.serviceAreas.length : 0,
    phases: Array.isArray(file.phases) ? file.phases.length : 0
  };
}
